<template>
  <div class="oauth-buttons">
    <button
      v-if="showsProvider('google')"
      type="button"
      class="oauth-btn google-btn"
      :disabled="loading"
      @click="signInWithGoogle"
    >
      <i class="fab fa-google"></i>
      <span>{{ mode === 'link' ? 'Link Google' : 'Continue with Google' }}</span>
    </button>
    <button
      v-if="showsProvider('microsoft')"
      type="button"
      class="oauth-btn microsoft-btn"
      :disabled="loading"
      @click="signInWithMicrosoft"
    >
      <i class="fab fa-microsoft"></i>
      <span>{{ mode === 'link' ? 'Link Microsoft' : 'Continue with Microsoft' }}</span>
    </button>
    <button
      v-if="showLinkedIn"
      type="button"
      class="oauth-btn linkedin-btn"
      :disabled="loading"
      @click="signInWithLinkedIn"
    >
      <i class="fab fa-linkedin"></i>
      <span>Continue with LinkedIn</span>
    </button>
    <p v-if="error" class="oauth-error">{{ error }}</p>
  </div>
</template>

<script>
import auth from '@/services/auth'

// Public OAuth client identifiers (safe to ship in the bundle — they are not
// secrets). Kept here so every surface that offers one-click signup
// (Login.vue, VetMediaPublic.vue, ...) shares one source of truth.
const GOOGLE_CLIENT_ID = '738579302482-d72d97klrfoo3cdguk4pv1mnbmjb0bjp.apps.googleusercontent.com'
const MICROSOFT_CLIENT_ID = '3382dd6d-0814-4caa-a5e6-fafd36e48248'
const MICROSOFT_AUTHORITY = 'https://login.microsoftonline.com/6c1af1f0-0b5d-43bc-bfd6-721a805f40c9'
const LINKEDIN_CLIENT_ID = '78hn1mgn3qqjz0'

export default {
  name: 'OAuthButtons',
  props: {
    /** Org slug to assign new accounts to (whitelabel). */
    organizationSlug: {
      type: String,
      default: null,
    },
    /** LinkedIn uses a popup + postMessage round-trip; hide it where undesired. */
    showLinkedIn: {
      type: Boolean,
      default: false,
    },
    /** login = sign in / create; link = attach provider to the current user. */
    mode: {
      type: String,
      default: 'login',
    },
    providers: {
      type: Array,
      default: () => ['google', 'microsoft'],
    },
  },
  emits: ['success', 'error'],
  data() {
    return {
      loading: false,
      error: '',
    }
  },
  methods: {
    _fail(message) {
      this.error = message
      this.loading = false
      this.$emit('error', message)
    },
    _apiError(err, fallback) {
      return err.response?.data?.error || err.response?.data?.detail || err.message || fallback
    },
    showsProvider(name) {
      return this.providers.includes(name)
    },
    _succeed(data) {
      this.loading = false
      this.$emit('success', data)
    },

    async signInWithGoogle() {
      this.loading = true
      this.error = ''
      try {
        if (!window.google || !window.google.accounts) {
          throw new Error('Google sign-in is still loading. Please try again in a moment.')
        }
        const tokenResponse = await new Promise((resolve, reject) => {
          window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'openid email profile',
            callback: (resp) => {
              if (resp.access_token) resolve(resp)
              else reject(new Error('Failed to get Google access token'))
            },
            error_callback: (err) => reject(new Error(`Google sign-in error: ${err.error || 'cancelled'}`)),
          }).requestAccessToken()
        })
        const data = this.mode === 'link'
          ? await auth.linkProvider('google', tokenResponse.access_token)
          : await auth.loginWithGoogle(tokenResponse.access_token, this.organizationSlug)
        this._succeed(data)
      } catch (err) {
        this._fail(this._apiError(err, 'Google sign-in failed. Please try again.'))
      }
    },

    async signInWithMicrosoft() {
      this.loading = true
      this.error = ''
      try {
        if (!window.msal) {
          const { PublicClientApplication } = await import('@azure/msal-browser')
          window.msal = new PublicClientApplication({
            auth: {
              clientId: MICROSOFT_CLIENT_ID,
              authority: MICROSOFT_AUTHORITY,
              redirectUri: window.location.origin,
            },
          })
        }
        const response = await window.msal.loginPopup({
          scopes: ['openid', 'profile', 'email', 'User.Read'],
        })
        if (!response.accessToken) throw new Error('Failed to get Microsoft access token')
        const data = this.mode === 'link'
          ? await auth.linkProvider('microsoft', response.accessToken)
          : await auth.loginWithMicrosoft(response.accessToken, this.organizationSlug)
        this._succeed(data)
      } catch (err) {
        this._fail(this._apiError(err, 'Microsoft sign-in failed. Please try again.'))
      }
    },

    signInWithLinkedIn() {
      this.loading = true
      this.error = ''
      try {
        const backendOrigin = window.location.origin.replace(':3000', ':8000')
        const redirectUri = encodeURIComponent(`${backendOrigin}/api/users/auth/linkedin/callback/`)
        const scope = encodeURIComponent('openid profile email')
        const state = Math.random().toString(36).substring(7)
        localStorage.setItem('linkedin_oauth_state', state)

        const url = 'https://www.linkedin.com/oauth/v2/authorization?'
          + 'response_type=code&'
          + `client_id=${LINKEDIN_CLIENT_ID}&`
          + `redirect_uri=${redirectUri}&`
          + `scope=${scope}&`
          + `state=${state}`

        const popup = window.open(url, 'linkedin-oauth', 'width=500,height=600,scrollbars=yes,resizable=yes')

        const messageHandler = (event) => {
          if (event.origin !== backendOrigin) return
          if (event.data.type === 'LINKEDIN_AUTH_SUCCESS') {
            auth.setToken(event.data.tokens.access)
            auth.setRefreshToken(event.data.tokens.refresh)
            auth.setUser(event.data.user)
            window.removeEventListener('message', messageHandler)
            if (popup) popup.close()
            this._succeed({ user: event.data.user, created: event.data.created })
          } else if (event.data.type === 'LINKEDIN_AUTH_ERROR') {
            window.removeEventListener('message', messageHandler)
            if (popup) popup.close()
            this._fail(event.data.error || 'LinkedIn sign-in failed.')
          }
        }
        window.addEventListener('message', messageHandler)

        const checkClosed = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(checkClosed)
            window.removeEventListener('message', messageHandler)
            if (this.loading) this._fail('LinkedIn sign-in was cancelled.')
          }
        }, 1000)
      } catch (err) {
        this._fail(err.message || 'LinkedIn sign-in failed. Please try again.')
      }
    },
  },
}
</script>

<style scoped>
.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid #e1e5e9;
  border-radius: 10px;
  background: #fff;
  color: #333;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.oauth-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #d1d5db;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.oauth-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.oauth-btn i {
  font-size: 1.1rem;
}

.google-btn:hover:not(:disabled) { border-color: #4285f4; color: #4285f4; }
.microsoft-btn:hover:not(:disabled) { border-color: #0078d4; color: #0078d4; }
.linkedin-btn:hover:not(:disabled) { border-color: #0a66c2; color: #0a66c2; }

.oauth-error {
  color: #b91c1c;
  font-size: 0.85rem;
  margin: 4px 0 0;
}
</style>
